// "Needs your review": pinned items from project state (src/review.ts), each with approve, redo, play and dismiss.
// When something new lands here, the Director is told once, so it can remind the user at a natural pause.
// 2026-11-04 (Keaton's report: items popped in and the list shifted while he scrolled): the order is stable (src/review.ts
// arrangeTray); while the tray is being scrolled or pointed at, new arrivals wait behind an "N new" pill.
import { useEffect, useMemo, useRef, useState } from "react";
import * as act from "../actions";
import { stopAudio, toggleAudio } from "../audio";
import { LOGGED_LINE } from "../logged";
import { director } from "../director";
import { pauseStageNow, playNow } from "../player";
import { arrangeTray, newKeys, reviewItems, reviewSummary, type ReviewItem } from "../review";
import { dismissReview, useStore } from "../store";
import type { Project } from "../types";
import { RedoSheet } from "./Artifact";
import { Icon } from "./bits";
import { PaidButton } from "./PriceFirst";
import { RetryButton } from "./RetryButton";

/** The tray's items for these projects, live as jobs move. */
export function useReview(projects: Project[] | null | undefined): ReviewItem[] {
  const jobs = useStore((s) => s.jobs);
  const dismissed = useStore((s) => s.dismissed);
  return useMemo(() => (projects ? reviewItems(projects, Object.values(jobs), dismissed) : []), [projects, jobs, dismissed]);
}

const told = new Set<string>();
/** Tell the Director (once per new item) what's waiting, so it reminds the user. */
export function useReviewReminder(items: ReviewItem[]) {
  const status = useStore((s) => s.director);
  useEffect(() => {
    if (status === "off" || status === "error" || status === "connecting") return;
    const fresh = newKeys(items, told);
    if (!fresh.length) return;
    fresh.forEach((k) => told.add(k));
    const what = items.filter((i) => fresh.includes(i.key));
    // 2026-11-04 (Keaton's report: Home's reminder named a frame of another video): always say which video
    const videos = [...new Set(items.map((i) => i.projectTitle))];
    const where = videos.length === 1 ? ` for “${videos[0]}”` : ` across ${videos.length} videos`;
    director.context(
      `[app] Waiting for the user's review${where} (${items.length} in the "Needs your review" tray): ${reviewSummary(what)}. They stay pinned until approved, redone or dismissed. Remind the user briefly at a natural pause; don't interrupt them.`,
    );
  }, [items, status]);
}

/** Whether the user is using the tray: pointing at it, touching it, or scrolling (the page or the list) just now. */
function useTrayHold() {
  const [over, setOver] = useState(false);
  const [moving, setMoving] = useState(false);
  const timer = useRef(0);
  const nudge = () => {
    setMoving(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMoving(false), 1200);
  };
  useEffect(() => {
    window.addEventListener("scroll", nudge, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", nudge, { capture: true });
      window.clearTimeout(timer.current);
    };
  }, []);
  const handlers = {
    onPointerEnter: (e: { pointerType: string }) => e.pointerType === "mouse" && setOver(true),
    onPointerLeave: () => setOver(false),
    onPointerDown: nudge,
  };
  return { hold: over || moving, handlers };
}

export function ReviewTray({ items, showProject = false, onOpen, title = "Needs your review", limit = 6 }: { items: ReviewItem[]; showProject?: boolean; onOpen?: (i: ReviewItem) => void; title?: string; limit?: number }) {
  const [all, setAll] = useState(false);
  const { hold, handlers } = useTrayHold();
  // first-seen order, kept across renders (arrangeTray is pure and gives the same answer twice, so strict mode is fine)
  const order = useRef<string[]>([]);
  const [revealed, setRevealed] = useState(0);
  const view = useMemo(() => arrangeTray(items, order.current, hold), [items, hold, revealed]); // eslint-disable-line react-hooks/exhaustive-deps
  order.current = view.order;
  const reveal = () => {
    order.current = [...view.order, ...view.fresh.map((i) => i.key)];
    setAll(true);
    setRevealed((n) => n + 1);
  };
  const count = view.shown.length + view.fresh.length;
  const shown = all ? view.shown : view.shown.slice(0, limit);
  return (
    <section className={`tray${items.length ? "" : " tray-empty"}`} aria-labelledby="tray-h" {...handlers}>
      <header className="tray-head">
        <h2 id="tray-h">{title}</h2>
        <span className={`count${items.length ? " is-on" : ""}`} aria-label={`${count} waiting`}>
          {count}
        </span>
        {!!view.fresh.length && (
          <button type="button" className="tray-new" onClick={reveal} aria-label={`Show ${view.fresh.length} new`}>
            {view.fresh.length} new
          </button>
        )}
      </header>
      {!items.length && <p className="tray-none">Nothing waiting on you. New frames, clips and music land here until you approve them.</p>}
      {!!items.length && (
        <ul className="tray-list">
          {shown.map((i) => (
            <li key={i.key}>
              <TrayRow item={i} showProject={showProject} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}
      {view.shown.length > limit && (
        <button type="button" className="btn btn-quiet btn-small tray-more" onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show all ${view.shown.length}`}
        </button>
      )}
    </section>
  );
}

function TrayRow({ item: i, showProject, onOpen }: { item: ReviewItem; showProject: boolean; onOpen?: (i: ReviewItem) => void }) {
  const [busy, setBusy] = useState(false);
  const [redo, setRedo] = useState(false);
  const key = `tray-${i.key}`;
  // 2026-11-04: "playing" whether the tray's own player or the shared stage (the Director's play) has this bed on
  const own = useStore((s) => s.playing === key);
  const staged = useStore((s) => {
    const st = s.nowPlaying.stage;
    return !!st && !st.paused && ((!!i.assetId && st.assetId === i.assetId) || (!!i.url && st.url === i.url));
  });
  const playing = own || staged;
  // a row that goes away (approved, redone) takes its sound with it
  useEffect(() => () => stopAudio(key), [key]);
  const run = (f: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await f();
    } finally {
      setBusy(false);
    }
  };
  const approve = () => (i.kind === "music" ? act.approveMusic(i.projectId) : i.sceneId && (i.kind === "frame" || i.kind === "clip") ? act.approve(i.projectId, i.sceneId, i.kind) : Promise.resolve());
  const thumb = i.kind === "frame" ? i.url : i.poster;
  return (
    <article className={`trow trow-${i.kind}`}>
      <button data-action="tray.open" type="button" className="trow-thumb" onClick={() => (onOpen ? onOpen(i) : i.kind === "clip" && i.url ? playNow({ url: i.url, title: i.title, what: "clip", projectId: i.projectId, sceneId: i.sceneId }) : undefined)} aria-label={`Open ${i.title}`}>
        {thumb ? <img src={thumb} alt="" /> : <span className="trow-glyph"><Icon name={i.kind === "music" ? "music" : i.kind === "failed" ? "alert" : "image"} size={18} /></span>}
        {i.kind === "clip" && (
          <span className="trow-play" aria-hidden>
            <Icon name="play" size={12} />
          </span>
        )}
      </button>
      <div className="trow-text">
        <p className="trow-title">
          {i.title}
          {showProject && <span className="trow-project">{i.projectTitle}</span>}
        </p>
        <p className={`trow-note${i.kind === "failed" ? " is-bad" : ""}`}>{i.kind === "failed" ? (i.logged ? `${i.note ?? ""} ${LOGGED_LINE}`.trim() : i.note) : i.kind === "music" ? "Listen, then keep it or redo it" : "Waiting for your OK"}</p>
        <div className="trow-acts">
          {/* PRICE-FIRST: the suggested model's price is on the button and sent with the yes; video asks first */}
          {i.kind === "failed" && i.suggestion && i.sceneId && i.failedKind === "clip" && (
            <PaidButton
              price={{ step: "clip", credits: i.suggestion.credits, clips: 1, needsYes: true, model: i.suggestion.model }}
              label={`Try ${i.suggestion.label}`}
              className="btn btn-primary btn-small"
              dataAction="scene.try-model"
              disabled={busy}
              ask={{ title: `Try ${i.suggestion.label} on scene ${i.sceneIndex ?? ""}?`.replace(" ?", "?"), lede: `A new clip of this scene on ${i.suggestion.label}.`, go: `Try ${i.suggestion.label}` }}
              run={(credits) => act.startStep(i.projectId, "clip", { sceneId: i.sceneId!, model: i.suggestion!.model }, credits)}
            />
          )}
          {i.kind === "failed" && !(i.suggestion && i.failedKind === "clip") && i.failedKind && (
            <RetryButton projectId={i.projectId} sceneId={i.sceneId ?? null} sceneIndex={i.sceneIndex} kind={i.failedKind} />
          )}
          {i.kind !== "failed" && (
            <button data-action="tray.approve" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={run(approve)}>
              <Icon name="check" size={14} /> {i.kind === "music" ? "Keep it" : "Approve"}
            </button>
          )}
          {i.kind === "music" && (
            <button type="button" className={`icon-btn${playing ? " is-on" : ""}`} aria-label={playing ? "Pause the music bed" : "Play the music bed"} onClick={() => (staged && !own ? pauseStageNow() : toggleAudio(key, i.url, `Music bed, ${i.projectTitle}`))}>
              <Icon name={playing ? "pause" : "play"} size={16} />
            </button>
          )}
          {(i.kind === "frame" || i.kind === "clip") && i.sceneId && (
            <button data-action="tray.scratch" type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={run(() => act.scratch(i.projectId, i.sceneId!, i.kind as "frame" | "clip"))} title="Throw this take away (an earlier one comes back)">
              <Icon name="trash" size={14} /> Scratch it
            </button>
          )}
          {i.kind !== "failed" && (
            <button data-action="tray.redo" type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={() => setRedo(true)}>
              <Icon name="redo" size={14} /> Redo
            </button>
          )}
        </div>
      </div>
      <button data-action="tray.dismiss" type="button" className="icon-btn trow-x" aria-label={`Dismiss ${i.title}`} title="Dismiss" onClick={() => dismissReview(i.key)}>
        <Icon name="close" size={16} />
      </button>
      {redo && (
        <RedoSheet
          a={{ kind: i.kind === "music" ? "audio" : i.kind === "clip" ? "clip" : "frame", projectId: i.projectId, sceneId: i.sceneId, title: i.title }}
          onClose={() => setRedo(false)}
          onRedo={async (note, expectCredits) => {
            setBusy(true);
            try {
              // PRICE-FIRST: the redo's own price was on the sheet's button; a dearer one comes back to ask again
              return await act.startStep(i.projectId, i.kind === "music" ? "music" : i.kind === "clip" ? "clip" : "frame", { sceneId: i.sceneId, redo: true, note }, expectCredits);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </article>
  );
}
